/**
 * "Save my pictures to my computer."
 *
 * Everything else in this app protects against losing a picture *inside* the
 * browser. This is the only thing that survives the browser itself — a cleared
 * profile, a reset tablet, a device left on a train. The zip holds a full-size
 * PNG of every picture, openable by anything, plus the scene JSON beside it so
 * the marks themselves are backed up and not just a flattened image.
 */

import type { Picture } from './store.ts';
import type { Scene } from './scene.ts';
import { decodeScene } from './scene.ts';
import { renderScene } from '../engine/snapshot.ts';
import type { ZipEntry } from './zip.ts';
import { makeZip } from './zip.ts';

const FULL_SIZE = 1600;

/** Decode the payload of a `data:` URL into bytes. */
export function dataUrlToBytes(url: string): Uint8Array {
  const comma = url.indexOf(',');
  if (comma < 0) return new Uint8Array(0);
  const base64 = url.slice(comma + 1);
  const binary = atob(base64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

function stampOf(at: number): string {
  const d = new Date(at);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Stable, sortable, and readable in a file manager without opening anything. */
export function entryName(picture: Picture, index: number): string {
  return `${stampOf(picture.at)}-${String(index + 1).padStart(3, '0')}-${picture.kind}`;
}

/**
 * Re-render at full size where we still have the marks; fall back to the
 * stored thumbnail for pictures kept before scenes existed, so an old drawing
 * is still exported rather than skipped.
 */
function imageFor(picture: Picture, background: string): { bytes: Uint8Array; ext: string } {
  const scene: Scene | null = picture.scene ? decodeScene(picture.scene) : null;
  if (scene) {
    const shot = renderScene(scene, FULL_SIZE, background);
    if (shot) return { bytes: dataUrlToBytes(shot.canvas.toDataURL('image/png')), ext: 'png' };
  }
  return { bytes: dataUrlToBytes(picture.thumb), ext: 'jpg' };
}

export function buildBackup(pictures: Picture[], background: string, now: Date): Uint8Array {
  const encoder = new TextEncoder();
  const entries: ZipEntry[] = [];

  pictures.forEach((picture, i) => {
    const name = entryName(picture, i);
    const image = imageFor(picture, background);
    entries.push({ name: `pictures/${name}.${image.ext}`, bytes: image.bytes });
    if (picture.scene) {
      entries.push({ name: `scenes/${name}.json`, bytes: encoder.encode(JSON.stringify(picture.scene)) });
    }
  });

  return makeZip(entries, now);
}

export function backupFilename(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `imagine-play-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.zip`;
}

/** Hand the file to the browser's downloads, then let go of the object URL. */
export function download(bytes: Uint8Array, filename: string): void {
  const blob = new Blob([bytes as unknown as BlobPart], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
